<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { ISector } from '@/interfaces/ISector';
  import { ITable, ITablesLayoutEntry } from '@/interfaces/ITable';

  const TABLE_SIZE_MIN = 1;
  const TABLE_SIZE_MAX = 8;

  const props = defineProps<{
    sector: ISector;
    /** Placed entries of this sector (posX/posY non-null). */
    entries: ITablesLayoutEntry[];
    tablesById: Map<number, ITable>;
    /**
     * Vue 2.7 can't runtime-validate `number | null` unions, so the parent
     * passes `undefined` instead of `null` for "no selection".
     */
    selectedTableId?: number;
    /** Blocks new drags (e.g. while the layout is being saved). */
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'select', tableId: number | null): void;
    (
      e: 'change',
      payload: { tableId: number; patch: Partial<ITablesLayoutEntry> }
    ): void;
    (e: 'toggle-shape', tableId: number): void;
    (e: 'unplace', tableId: number): void;
  }>();

  const containerRef = ref<HTMLElement | null>(null);
  const gridRef = ref<HTMLElement | null>(null);

  // Manual ResizeObserver: vueuse's useElementSize/useResizeObserver warn
  // under Vue 2.7 (their useMounted passes null as onMounted target).
  const containerW = ref(0);
  const windowH = ref(window.innerHeight);
  let resizeObserver: ResizeObserver | null = null;

  function onWindowResize() {
    windowH.value = window.innerHeight;
  }

  onMounted(() => {
    resizeObserver = new ResizeObserver(([entry]) => {
      containerW.value = entry.contentRect.width;
    });
    if (containerRef.value) resizeObserver.observe(containerRef.value);
    window.addEventListener('resize', onWindowResize);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    window.removeEventListener('resize', onWindowResize);
    if (flashTimeout) clearTimeout(flashTimeout);
  });

  // Width-driven sizing: the grid spans the full container width, capped by
  // a viewport-based height so tall grids don't produce endless canvases.
  const cellPx = computed(() => {
    if (!containerW.value) return 0;
    const maxGridH = Math.max(360, windowH.value * 0.72);
    return Math.min(
      containerW.value / props.sector.gridCols,
      maxGridH / props.sector.gridRows
    );
  });

  const gridStyle = computed(() => {
    const cell = cellPx.value;
    return {
      width: `${cell * props.sector.gridCols}px`,
      height: `${cell * props.sector.gridRows}px`,
      backgroundSize: `${cell}px ${cell}px`,
    };
  });

  //#region Drag (move / resize) with pointer events

  interface IDragState {
    tableId: number;
    pointerId: number;
    mode: 'move' | 'resize';
    startClientX: number;
    startClientY: number;
    origPosX: number;
    origPosY: number;
    origW: number;
    origH: number;
    posX: number;
    posY: number;
    w: number;
    h: number;
    valid: boolean;
    moved: boolean;
  }

  const drag = ref<IDragState | null>(null);
  const flashTableId = ref<number | null>(null);
  let flashTimeout: ReturnType<typeof setTimeout> | null = null;

  function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }

  /** Occupied cells of the sector, excluding one table (the dragged one). */
  function buildOccupancy(excludeTableId: number): Set<string> {
    const occupied = new Set<string>();
    for (const entry of props.entries) {
      if (entry.tableId === excludeTableId) continue;
      if (entry.posX === null || entry.posY === null) continue;
      for (let x = entry.posX; x < entry.posX + entry.w; x++) {
        for (let y = entry.posY; y < entry.posY + entry.h; y++) {
          occupied.add(`${x},${y}`);
        }
      }
    }
    return occupied;
  }

  function areaFree(
    occupied: Set<string>,
    posX: number,
    posY: number,
    w: number,
    h: number
  ): boolean {
    for (let x = posX; x < posX + w; x++) {
      for (let y = posY; y < posY + h; y++) {
        if (occupied.has(`${x},${y}`)) return false;
      }
    }
    return true;
  }

  function startDrag(
    event: PointerEvent,
    entry: ITablesLayoutEntry,
    mode: 'move' | 'resize'
  ) {
    if (event.button !== 0 || drag.value || props.disabled) return;
    if (entry.posX === null || entry.posY === null) return;

    emit('select', entry.tableId);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

    drag.value = {
      tableId: entry.tableId,
      pointerId: event.pointerId,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      origPosX: entry.posX,
      origPosY: entry.posY,
      origW: entry.w,
      origH: entry.h,
      posX: entry.posX,
      posY: entry.posY,
      w: entry.w,
      h: entry.h,
      valid: true,
      moved: false,
    };
  }

  function onTilePointerDown(event: PointerEvent, entry: ITablesLayoutEntry) {
    startDrag(event, entry, 'move');
  }

  function onResizePointerDown(event: PointerEvent, entry: ITablesLayoutEntry) {
    startDrag(event, entry, 'resize');
  }

  function onPointerMove(event: PointerEvent) {
    const d = drag.value;
    if (!d || d.pointerId !== event.pointerId || !cellPx.value) return;

    const dx = Math.round((event.clientX - d.startClientX) / cellPx.value);
    const dy = Math.round((event.clientY - d.startClientY) / cellPx.value);

    const { gridCols, gridRows } = props.sector;

    let posX = d.posX;
    let posY = d.posY;
    let w = d.w;
    let h = d.h;

    if (d.mode === 'move') {
      posX = clamp(d.origPosX + dx, 0, gridCols - d.w);
      posY = clamp(d.origPosY + dy, 0, gridRows - d.h);
    } else {
      w = clamp(
        d.origW + dx,
        TABLE_SIZE_MIN,
        Math.min(TABLE_SIZE_MAX, gridCols - d.origPosX)
      );
      h = clamp(
        d.origH + dy,
        TABLE_SIZE_MIN,
        Math.min(TABLE_SIZE_MAX, gridRows - d.origPosY)
      );
    }

    if (posX === d.posX && posY === d.posY && w === d.w && h === d.h) return;

    d.posX = posX;
    d.posY = posY;
    d.w = w;
    d.h = h;
    d.moved = true;
    d.valid = areaFree(buildOccupancy(d.tableId), posX, posY, w, h);
  }

  function onPointerUp(event: PointerEvent) {
    const d = drag.value;
    if (!d || d.pointerId !== event.pointerId) return;

    drag.value = null;

    if (!d.moved) return;

    if (!d.valid) {
      flash(d.tableId);
      return;
    }

    const patch: Partial<ITablesLayoutEntry> =
      d.mode === 'move' ? { posX: d.posX, posY: d.posY } : { w: d.w, h: d.h };

    const changed =
      d.mode === 'move'
        ? d.posX !== d.origPosX || d.posY !== d.origPosY
        : d.w !== d.origW || d.h !== d.origH;

    if (changed) emit('change', { tableId: d.tableId, patch });
  }

  /** A cancelled pointer (system gesture, palm rejection) reverts the drag. */
  function onPointerCancel(event: PointerEvent) {
    const d = drag.value;
    if (!d || d.pointerId !== event.pointerId) return;
    drag.value = null;
  }

  function flash(tableId: number) {
    flashTableId.value = tableId;
    if (flashTimeout) clearTimeout(flashTimeout);
    flashTimeout = setTimeout(() => {
      flashTableId.value = null;
    }, 450);
  }

  //#endregion

  //#region Rendering helpers

  function effectiveRect(entry: ITablesLayoutEntry) {
    const d = drag.value;
    return d && d.tableId === entry.tableId ? d : entry;
  }

  function tileStyle(entry: ITablesLayoutEntry) {
    const rect = effectiveRect(entry);
    const cell = cellPx.value;
    return {
      left: `${(rect.posX ?? 0) * cell}px`,
      top: `${(rect.posY ?? 0) * cell}px`,
      width: `${rect.w * cell}px`,
      height: `${rect.h * cell}px`,
    };
  }

  function tileClasses(entry: ITablesLayoutEntry) {
    const d = drag.value;
    const dragging = d?.tableId === entry.tableId;
    return {
      'is-round': entry.shape === 'round',
      'is-selected': entry.tableId === props.selectedTableId,
      'is-dragging': dragging,
      'is-invalid':
        (dragging && !d.valid) || flashTableId.value === entry.tableId,
      // Small tiles can't contain their label: switch to the overflowing
      // pill style so the name stays readable.
      'is-compact': effectiveRect(entry).w * cellPx.value < 48,
    };
  }

  function tableName(entry: ITablesLayoutEntry) {
    return props.tablesById.get(entry.tableId)?.name ?? `#${entry.tableId}`;
  }

  const selectedEntry = computed(
    () => props.entries.find((e) => e.tableId === props.selectedTableId) ?? null
  );

  /** Size after growing/shrinking both dimensions, clamped to grid bounds. */
  function resizedRect(entry: ITablesLayoutEntry, delta: -1 | 1) {
    const { gridCols, gridRows } = props.sector;
    return {
      w: clamp(
        entry.w + delta,
        TABLE_SIZE_MIN,
        Math.min(TABLE_SIZE_MAX, gridCols - (entry.posX ?? 0))
      ),
      h: clamp(
        entry.h + delta,
        TABLE_SIZE_MIN,
        Math.min(TABLE_SIZE_MAX, gridRows - (entry.posY ?? 0))
      ),
    };
  }

  function canResizeSelected(delta: -1 | 1): boolean {
    const entry = selectedEntry.value;
    if (!entry) return false;
    const { w, h } = resizedRect(entry, delta);
    return w !== entry.w || h !== entry.h;
  }

  const canShrinkSelected = computed(() => canResizeSelected(-1));
  const canEnlargeSelected = computed(() => canResizeSelected(1));

  /** Toolbar +/- buttons: same effect as the corner drag, one cell per click. */
  function resizeSelected(delta: -1 | 1) {
    const entry = selectedEntry.value;
    if (!entry || entry.posX === null || entry.posY === null || props.disabled)
      return;

    const { w, h } = resizedRect(entry, delta);
    if (w === entry.w && h === entry.h) return;

    if (
      delta > 0 &&
      !areaFree(buildOccupancy(entry.tableId), entry.posX, entry.posY, w, h)
    ) {
      flash(entry.tableId);
      return;
    }

    emit('change', { tableId: entry.tableId, patch: { w, h } });
  }

  const toolbarStyle = computed(() => {
    const entry = selectedEntry.value;
    if (!entry || entry.posX === null || entry.posY === null || drag.value)
      return null;
    const cell = cellPx.value;
    const top = entry.posY * cell - 40;
    return {
      left: `${entry.posX * cell + (entry.w * cell) / 2}px`,
      top: `${Math.max(top, 2)}px`,
    };
  });

  //#endregion

  function onCanvasClick() {
    emit('select', null);
  }

  /** Converts viewport coordinates into a grid cell (used for tray drops). */
  function cellAtPoint(
    clientX: number,
    clientY: number
  ): { x: number; y: number } | null {
    const el = gridRef.value;
    if (!el || !cellPx.value) return null;

    const rect = el.getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX >= rect.right ||
      clientY < rect.top ||
      clientY >= rect.bottom
    ) {
      return null;
    }

    return {
      x: Math.floor((clientX - rect.left) / cellPx.value),
      y: Math.floor((clientY - rect.top) / cellPx.value),
    };
  }

  defineExpose({ cellAtPoint });
</script>

<template>
  <div ref="containerRef" class="floor-plan-canvas" @click.self="onCanvasClick">
    <div
      ref="gridRef"
      class="canvas-grid"
      :style="gridStyle"
      @click.self="onCanvasClick"
    >
      <div
        v-for="entry in entries"
        :key="entry.tableId"
        class="canvas-tile"
        :class="tileClasses(entry)"
        :style="tileStyle(entry)"
        @pointerdown="onTilePointerDown($event, entry)"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerCancel"
      >
        <span class="canvas-tile-name">{{ tableName(entry) }}</span>
        <div
          v-if="entry.tableId === selectedTableId"
          class="canvas-tile-resize"
          @pointerdown.stop="onResizePointerDown($event, entry)"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerCancel"
        >
          <v-icon x-small>mdi-resize-bottom-right</v-icon>
        </div>
      </div>

      <div
        v-if="toolbarStyle && selectedEntry"
        class="canvas-toolbar"
        :style="toolbarStyle"
      >
        <v-btn
          icon
          small
          :disabled="disabled || !canShrinkSelected"
          :title="$t('floorPlan.shrinkTable')"
          @click.stop="resizeSelected(-1)"
        >
          <v-icon small>mdi-minus</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :disabled="disabled || !canEnlargeSelected"
          :title="$t('floorPlan.enlargeTable')"
          @click.stop="resizeSelected(1)"
        >
          <v-icon small>mdi-plus</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :title="$t('floorPlan.toggleShape')"
          @click.stop="emit('toggle-shape', selectedEntry.tableId)"
        >
          <v-icon small>
            {{
              selectedEntry.shape === 'round'
                ? 'mdi-square-outline'
                : 'mdi-circle-outline'
            }}
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :title="$t('floorPlan.removeFromPlan')"
          @click.stop="emit('unplace', selectedEntry.tableId)"
        >
          <v-icon small color="error">mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .floor-plan-canvas {
    @apply tw-flex tw-items-center tw-justify-center tw-w-full tw-bg-neutral-50 tw-rounded-xl tw-border tw-border-gray-100 tw-p-2;
    min-height: 240px;

    .canvas-grid {
      @apply tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-gray-200;
      background-image: linear-gradient(
          to right,
          rgba(0, 0, 0, 0.045) 1px,
          transparent 1px
        ),
        linear-gradient(to bottom, rgba(0, 0, 0, 0.045) 1px, transparent 1px);

      .canvas-tile {
        @apply tw-absolute tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-emerald-300 tw-bg-emerald-50 tw-cursor-grab tw-select-none tw-transition-colors;
        touch-action: none;
        z-index: 1;

        &.is-round {
          @apply tw-rounded-full;
        }

        &.is-selected {
          @apply tw-border-orange-400 tw-bg-orange-50;
          z-index: 2;
        }

        &.is-dragging {
          @apply tw-cursor-grabbing tw-shadow-lg;
          z-index: 3;
        }

        &.is-invalid {
          @apply tw-border-red-400 tw-bg-red-100;
        }

        .canvas-tile-name {
          /* Never hide the name: overflow the tile instead of truncating. */
          @apply tw-text-xs tw-font-semibold tw-text-gray-700 tw-px-1 tw-pointer-events-none tw-whitespace-nowrap;
        }

        &.is-compact .canvas-tile-name {
          @apply tw-rounded tw-px-0.5;
          font-size: 10px;
          background: rgba(255, 255, 255, 0.85);
        }

        .canvas-tile-resize {
          @apply tw-absolute tw-bottom-0 tw-right-0 tw-p-0.5 tw-text-gray-500;
          cursor: nwse-resize;
          touch-action: none;
        }
      }

      .canvas-toolbar {
        @apply tw-absolute tw-flex tw-items-center tw-gap-0.5 tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-md tw-px-1 tw-py-0.5;
        transform: translateX(-50%);
        z-index: 4;
      }
    }
  }
</style>
